import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE, readStoredToken } from "./api";

interface AdminSummary {
  total_customers: number;
  total_managed_load_kw: number;
  total_savings_usd: number;
  total_co2_avoided_kg: number;
  total_co2_epa_metric_tons?: number;
  total_carbon_credits?: number;
  total_carbon_value_usd?: number;
  region_customer_counts?: Record<string, number>;
  region_savings_usd?: Record<string, number>;
}

interface AdminCustomerRow {
  user_id: number;
  company_name: string;
  email: string;
  registered_at: string;
  total_savings_usd: number;
  last_active_at: string | null;
  feed_status: string;
  watchdog_active: boolean;
  job_count: number;
  managed_load_kw: number;
  monthly_savings_usd: number;
  last_report_sent_at?: string | null;
  prediction_accuracy_pct?: number;
  carbon_credits_total?: number;
  carbon_value_usd_total?: number;
  carbon_credits_month?: number;
  carbon_value_usd_month?: number;
  market_region?: string;
}

interface AdminOverview {
  summary: AdminSummary;
  customers: AdminCustomerRow[];
}

interface HealthFactorBreakdown {
  score: number;
  max: number;
  reason: string;
}

interface AdminHealthCustomer {
  user_id: number;
  company_name: string;
  email: string;
  score: number;
  band: "healthy" | "needs_attention" | "at_risk";
  breakdown: Record<string, HealthFactorBreakdown>;
}

interface AdminHealthSummary {
  healthy: number;
  needs_attention: number;
  at_risk: number;
}

interface AdminHealthPayload {
  summary: AdminHealthSummary;
  customers: AdminHealthCustomer[];
}

interface BillingInvoice {
  invoice_id: string;
  customer_id: number;
  billing_period: string;
  total_savings: number;
  fee_percentage: number;
  fee_amount: number;
  status: "draft" | "sent" | "paid" | "overdue";
}

interface AdminBillingSummary {
  mrr_paid_current_month: number;
  paid_invoices_current_month: number;
  total_invoices: number;
}

interface AdminBillingPayload {
  summary: AdminBillingSummary;
  invoices: BillingInvoice[];
}

interface AdminAgentEvent {
  event_type: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

interface AdminAgentRow {
  customer_id: number;
  customer_email: string;
  company_name?: string | null;
  token_name: string | null;
  is_online: boolean;
  last_seen: string | null;
  webhook_registered: boolean;
  mode: string | null;
  scheduler: string | null;
  total_events: number;
  total_pauses: number;
  response_time_avg_ms: number;
  last_action: string | null;
  last_action_time: string | null;
  status: "online" | "offline" | "not_connected";
  recent_events: AdminAgentEvent[];
}

interface AccessRequestRow {
  id: number;
  email: string;
  company_name: string;
  use_case: string;
  estimated_gpu_count: string;
  region: string;
  created_at: string;
  status: "pending" | "approved" | "rejected";
  reviewed_at?: string | null;
  reviewed_by?: number | null;
}

interface AccessRequestsPayload {
  pending_count: number;
  requests: AccessRequestRow[];
}

type AdminTab = "overview" | "agents" | "access-requests";

const cardStyle: React.CSSProperties = {
  background: "#13213A",
  border: "1px solid #1F355B",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
};

function formatTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function AdminDashboard() {
  const now = new Date();
  const defaultBillingPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [token] = useState(() => readStoredToken());
  const [data, setData] = useState<AdminOverview | null>(null);
  const [billing, setBilling] = useState<AdminBillingPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingRunning, setBillingRunning] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState(defaultBillingPeriod);
  const [reportPeriod, setReportPeriod] = useState(defaultBillingPeriod);
  const [billingCustomerFilter, setBillingCustomerFilter] = useState<string>("all");
  const [billingCustomerGenerate, setBillingCustomerGenerate] = useState<string>("all");
  const [health, setHealth] = useState<AdminHealthPayload | null>(null);
  const [expandedHealthRows, setExpandedHealthRows] = useState<Record<number, boolean>>({});
  const [adminTab, setAdminTab] = useState<AdminTab>("overview");
  const [agents, setAgents] = useState<AdminAgentRow[]>([]);
  const [expandedAgentRows, setExpandedAgentRows] = useState<Record<number, boolean>>({});
  const [accessRequests, setAccessRequests] = useState<AccessRequestsPayload | null>(null);
  const [accessActionId, setAccessActionId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Please sign in from the main dashboard first, then open /admin again.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 403) {
          setError("Admin access required. Your account is not configured as an admin.");
          return;
        }
        if (res.status === 401) {
          setError("Session expired. Return to the dashboard and sign in again.");
          return;
        }
        if (!res.ok) {
          setError("Could not load admin overview.");
          return;
        }
        const json = (await res.json()) as AdminOverview;
        setData(json);
        const billingRes = await fetch(`${API_BASE}/admin/billing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (billingRes.ok) {
          const b = (await billingRes.json()) as AdminBillingPayload;
          setBilling(b);
        }
        const healthRes = await fetch(`${API_BASE}/admin/health`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (healthRes.ok) {
          const h = (await healthRes.json()) as AdminHealthPayload;
          setHealth(h);
        }
        const agentsRes = await fetch(`${API_BASE}/api/admin/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (agentsRes.ok) {
          setAgents((await agentsRes.json()) as AdminAgentRow[]);
        }
        const accessRes = await fetch(`${API_BASE}/api/admin/access-requests`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (accessRes.ok) {
          setAccessRequests((await accessRes.json()) as AccessRequestsPayload);
        }
      } catch {
        if (!cancelled) setError("Network error loading admin overview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const markPaid = async (invoiceId: string) => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}/mark-paid`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const filterQuery =
      billingCustomerFilter !== "all" ? `?customer_id=${encodeURIComponent(billingCustomerFilter)}` : "";
    const next = await fetch(`${API_BASE}/admin/billing${filterQuery}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (next.ok) setBilling((await next.json()) as AdminBillingPayload);
  };

  const sendInvoice = async (invoiceId: string) => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/admin/billing/${encodeURIComponent(invoiceId)}/send`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      setError("Could not send invoice.");
      return;
    }
    const filterQuery =
      billingCustomerFilter !== "all" ? `?customer_id=${encodeURIComponent(billingCustomerFilter)}` : "";
    const next = await fetch(`${API_BASE}/admin/billing${filterQuery}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (next.ok) setBilling((await next.json()) as AdminBillingPayload);
  };

  const deleteInvoice = async (invoiceId: string) => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/admin/billing/${encodeURIComponent(invoiceId)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      setError("Could not delete invoice.");
      return;
    }
    const filterQuery =
      billingCustomerFilter !== "all" ? `?customer_id=${encodeURIComponent(billingCustomerFilter)}` : "";
    const next = await fetch(`${API_BASE}/admin/billing${filterQuery}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (next.ok) setBilling((await next.json()) as AdminBillingPayload);
  };

  const runBillingNow = async () => {
    if (!token) return;
    setBillingRunning(true);
    setError(null);
    try {
      const period = billingPeriod.trim();
      if (!/^\d{4}-\d{2}$/.test(period)) {
        setError("Enter billing period as YYYY-MM.");
        return;
      }
      const customerParam =
        billingCustomerGenerate !== "all"
          ? `&customer_id=${encodeURIComponent(billingCustomerGenerate)}`
          : "";
      const run = await fetch(
        `${API_BASE}/admin/billing/generate-now?period=${encodeURIComponent(period)}${customerParam}`,
        {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!run.ok) {
        setError("Could not run billing now.");
        return;
      }
      const filterQuery =
        billingCustomerFilter !== "all" ? `?customer_id=${encodeURIComponent(billingCustomerFilter)}` : "";
      const next = await fetch(`${API_BASE}/admin/billing${filterQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (next.ok) setBilling((await next.json()) as AdminBillingPayload);
    } catch {
      setError("Network error while running billing.");
    } finally {
      setBillingRunning(false);
    }
  };

  const sendReportNow = async (customerId: number) => {
    if (!token) return;
    const period = reportPeriod.trim();
    if (!/^\d{4}-\d{2}$/.test(period)) {
      setError("Enter report period as YYYY-MM.");
      return;
    }
    const res = await fetch(
      `${API_BASE}/admin/reports/send-now?customer_id=${encodeURIComponent(String(customerId))}&period=${encodeURIComponent(period)}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!res.ok) {
      setError("Could not send report.");
      return;
    }
    const overview = await fetch(`${API_BASE}/api/admin/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (overview.ok) setData((await overview.json()) as AdminOverview);
    const healthRes = await fetch(`${API_BASE}/admin/health`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (healthRes.ok) setHealth((await healthRes.json()) as AdminHealthPayload);
  };

  useEffect(() => {
    if (!token) return;
    const loadFilteredBilling = async () => {
      const filterQuery =
        billingCustomerFilter !== "all"
          ? `?customer_id=${encodeURIComponent(billingCustomerFilter)}`
          : "";
      const res = await fetch(`${API_BASE}/admin/billing${filterQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setBilling((await res.json()) as AdminBillingPayload);
    };
    void loadFilteredBilling();
  }, [token, billingCustomerFilter]);

  const reloadAccessRequests = async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/api/admin/access-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setAccessRequests((await res.json()) as AccessRequestsPayload);
    }
  };

  const approveAccessRequest = async (requestId: number) => {
    if (!token) return;
    const company =
      accessRequests?.requests.find((r) => r.id === requestId)?.company_name ?? "this company";
    if (!window.confirm(`Approve access for ${company}? A welcome email will be sent.`)) {
      return;
    }
    setAccessActionId(requestId);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/access-requests/${encodeURIComponent(String(requestId))}/approve`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError((data as { detail?: string }).detail || "Could not approve request.");
        return;
      }
      await reloadAccessRequests();
    } catch {
      setError("Network error while approving request.");
    } finally {
      setAccessActionId(null);
    }
  };

  const rejectAccessRequest = async (requestId: number) => {
    if (!token) return;
    if (!window.confirm("Reject this access request?")) {
      return;
    }
    const sendEmail = window.confirm("Send a rejection email to the applicant?");
    setAccessActionId(requestId);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/access-requests/${encodeURIComponent(String(requestId))}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ send_email: sendEmail }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError((data as { detail?: string }).detail || "Could not reject request.");
        return;
      }
      await reloadAccessRequests();
    } catch {
      setError("Network error while rejecting request.");
    } finally {
      setAccessActionId(null);
    }
  };

  const accessStatusBadge = (status: AccessRequestRow["status"]): React.CSSProperties => {
    if (status === "pending") {
      return { background: "#422006", color: "#FDE68A", border: "1px solid #D97706" };
    }
    if (status === "approved") {
      return { background: "#052E16", color: "#86EFAC", border: "1px solid #16A34A" };
    }
    return { background: "#450A0A", color: "#FCA5A5", border: "1px solid #DC2626" };
  };

  const healthByUser = new Map<number, AdminHealthCustomer>(
    (health?.customers ?? []).map((h) => [h.user_id, h])
  );
  const sortedCustomers = [...(data?.customers ?? [])].sort((a, b) => {
    const ah = healthByUser.get(a.user_id)?.score ?? 999;
    const bh = healthByUser.get(b.user_id)?.score ?? 999;
    return ah - bh;
  });

  const badgeStyle = (band: "healthy" | "needs_attention" | "at_risk" | undefined): React.CSSProperties => {
    if (band === "healthy") return { background: "#052E16", color: "#86EFAC", border: "1px solid #16A34A" };
    if (band === "needs_attention") return { background: "#422006", color: "#FDE68A", border: "1px solid #D97706" };
    return { background: "#450A0A", color: "#FCA5A5", border: "1px solid #DC2626" };
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0A1628",
        color: "#E2E8F0",
        padding: 24,
        fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gap: 18 }}>
        <div style={{ ...cardStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: "#F8FAFC" }}>EloPlus Admin</h1>
            <div style={{ marginTop: 6, fontSize: 13, color: "#94A3B8" }}>
              Cross-customer health, load, savings, and CO₂ impact
            </div>
          </div>
          <Link
            to="/"
            style={{
              color: "#0EA5A0",
              fontWeight: 700,
              textDecoration: "none",
              border: "1px solid #0EA5A0",
              borderRadius: 10,
              padding: "10px 16px",
            }}
          >
            ← Dashboard
          </Link>
        </div>


        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {(
            [
              ["overview", "Overview"],
              ["agents", "Agents"],
              ["access-requests", "Access Requests"],
            ] as const
          ).map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setAdminTab(tab)}
              style={{
                background: adminTab === tab ? "#0EA5A0" : "#1E293B",
                color: adminTab === tab ? "#042F2E" : "#E2E8F0",
                border: `1px solid ${adminTab === tab ? "#0EA5A0" : "#334155"}`,
                borderRadius: 10,
                padding: "10px 16px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && <div style={{ color: "#94A3B8" }}>Loading overview…</div>}
        {error && (
          <div style={{ ...cardStyle, color: "#FCA5A5", borderColor: "#7F1D1D" }}>
            {error}
            <div style={{ marginTop: 12 }}>
              <Link to="/" style={{ color: "#67E8F9" }}>
                Go to main app
              </Link>
            </div>
          </div>
        )}

        {data && !error && adminTab === "overview" && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 14,
              }}
            >
              {[
                { label: "Total customers", value: data.summary.total_customers.toLocaleString() },
                {
                  label: "Total managed load",
                  value: `${data.summary.total_managed_load_kw.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW`,
                },
                {
                  label: "Total savings (lifetime, all tenants)",
                  value: `$${data.summary.total_savings_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                },
                {
                  label: "Total CO₂ avoided",
                  value: `${data.summary.total_co2_avoided_kg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`,
                },
                {
                  label: "EPA spike CO₂ (all tenants)",
                  value: `${(data.summary.total_co2_epa_metric_tons ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} metric tons`,
                },
                {
                  label: "Carbon credits earned",
                  value: `${(data.summary.total_carbon_credits ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} credits`,
                },
                {
                  label: "Est. carbon credit value",
                  value: `$${(data.summary.total_carbon_value_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                },
              ].map((item) => (
                <div key={item.label} style={cardStyle}>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#F8FAFC" }}>{item.value}</div>
                </div>
              ))}
              {(["ERCOT", "PJM", "CAISO"] as const).map((iso) => {
                const counts = data.summary.region_customer_counts ?? {};
                const savings = data.summary.region_savings_usd ?? {};
                const n = counts[iso] ?? 0;
                const s = savings[iso] ?? 0;
                return (
                  <div key={`region-${iso}`} style={cardStyle}>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>{iso} customers</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#F8FAFC" }}>{n}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Lifetime savings</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#4ADE80" }}>
                      ${s.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                );
              })}
              {health && (
                <>
                  <div style={cardStyle}>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>Healthy</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#22C55E" }}>{health.summary.healthy}</div>
                  </div>
                  <div style={cardStyle}>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>Needs attention</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#FACC15" }}>{health.summary.needs_attention}</div>
                  </div>
                  <div style={cardStyle}>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>At risk</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#EF4444" }}>{health.summary.at_risk}</div>
                  </div>
                </>
              )}
            </div>

            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #1F355B", color: "#F8FAFC", fontWeight: 700 }}>
                Customers
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#0B1220", color: "#94A3B8", textAlign: "left" }}>
                      <th style={{ padding: "10px 14px" }}>Company</th>
                      <th style={{ padding: "10px 14px" }}>Region</th>
                      <th style={{ padding: "10px 14px" }}>Email</th>
                      <th style={{ padding: "10px 14px" }}>Registered</th>
                      <th style={{ padding: "10px 14px" }}>Total savings</th>
                      <th style={{ padding: "10px 14px" }}>Last active</th>
                      <th style={{ padding: "10px 14px" }}>Feed</th>
                      <th style={{ padding: "10px 14px" }}>Watchdog</th>
                      <th style={{ padding: "10px 14px" }}>Jobs</th>
                      <th style={{ padding: "10px 14px" }}>Load (kW)</th>
                      <th style={{ padding: "10px 14px" }}>This month</th>
                      <th style={{ padding: "10px 14px" }}>Prediction accuracy</th>
                      <th style={{ padding: "10px 14px" }}>Carbon credits</th>
                      <th style={{ padding: "10px 14px" }}>Carbon value</th>
                      <th style={{ padding: "10px 14px" }}>Health</th>
                      <th style={{ padding: "10px 14px" }}>Last report sent</th>
                      <th style={{ padding: "10px 14px" }}>Report action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCustomers.map((c) => {
                      const hc = healthByUser.get(c.user_id);
                      const isOpen = expandedHealthRows[c.user_id] === true;
                      return (
                      <React.Fragment key={c.user_id}>
                      <tr style={{ borderTop: "1px solid #1F355B" }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: "#F1F5F9" }}>{c.company_name}</td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8", fontWeight: 600 }}>
                          {c.market_region ?? "ERCOT"}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#CBD5E1" }}>{c.email}</td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{formatTs(c.registered_at)}</td>
                        <td style={{ padding: "10px 14px", color: "#4ADE80" }}>
                          ${c.total_savings_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{formatTs(c.last_active_at)}</td>
                        <td style={{ padding: "10px 14px", color: "#67E8F9" }}>{c.feed_status}</td>
                        <td style={{ padding: "10px 14px", color: c.watchdog_active ? "#4ADE80" : "#94A3B8" }}>
                          {c.watchdog_active ? "Yes" : "No"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>{c.job_count}</td>
                        <td style={{ padding: "10px 14px" }}>
                          {c.managed_load_kw.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#FACC15" }}>
                          ${c.monthly_savings_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#67E8F9" }}>
                          {(c.prediction_accuracy_pct ?? 0).toFixed(1)}%
                        </td>
                        <td style={{ padding: "10px 14px", color: "#6EE7B7", fontVariantNumeric: "tabular-nums" }}>
                          {(c.carbon_credits_total ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#A7F3D0", fontVariantNumeric: "tabular-nums" }}>
                          ${(c.carbon_value_usd_total ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedHealthRows((prev) => ({ ...prev, [c.user_id]: !prev[c.user_id] }))
                            }
                            style={{
                              ...badgeStyle(hc?.band),
                              borderRadius: 999,
                              padding: "5px 10px",
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            {hc ? `${hc.score}` : "—"}
                          </button>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>
                          {formatTs(c.last_report_sent_at)}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <button
                            type="button"
                            onClick={() => void sendReportNow(c.user_id)}
                            style={{
                              border: "1px solid #0EA5A0",
                              background: "#042F2E",
                              color: "#99F6E4",
                              borderRadius: 8,
                              padding: "6px 10px",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Send report now
                          </button>
                        </td>
                      </tr>
                      {hc && isOpen && (
                        <tr style={{ background: "#0B1220" }}>
                          <td colSpan={17} style={{ padding: "10px 14px" }}>
                            <div style={{ display: "grid", gap: 6, color: "#CBD5E1", fontSize: 12 }}>
                              {Object.entries(hc.breakdown).map(([k, v]) => (
                                <div key={`${c.user_id}-${k}`}>
                                  <strong>{k.replace(/_/g, " ")}:</strong> {v.score}/{v.max} — {v.reason}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>

            {billing && (
              <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #1F355B", color: "#F8FAFC", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <span>
                    Billing (MRR: ${billing.summary.mrr_paid_current_month.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                      value={billingCustomerFilter}
                      onChange={(event) => setBillingCustomerFilter(event.target.value)}
                      style={{
                        border: "1px solid #334155",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        fontWeight: 600,
                      }}
                      title="Filter billing table customer"
                    >
                      <option value="all">All customers</option>
                      {data.customers.map((c) => (
                        <option key={`filter-${c.user_id}`} value={String(c.user_id)}>
                          {c.company_name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={billingPeriod}
                      onChange={(event) => setBillingPeriod(event.target.value)}
                      placeholder="YYYY-MM"
                      style={{
                        width: 92,
                        border: "1px solid #334155",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        fontWeight: 600,
                      }}
                    />
                    <input
                      type="text"
                      value={reportPeriod}
                      onChange={(event) => setReportPeriod(event.target.value)}
                      placeholder="Report YYYY-MM"
                      style={{
                        width: 120,
                        border: "1px solid #334155",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        fontWeight: 600,
                      }}
                    />
                    <select
                      value={billingCustomerGenerate}
                      onChange={(event) => setBillingCustomerGenerate(event.target.value)}
                      style={{
                        border: "1px solid #334155",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        fontWeight: 600,
                      }}
                      title="Generate drafts for selected customer"
                    >
                      <option value="all">Generate: all</option>
                      {data.customers.map((c) => (
                        <option key={`gen-${c.user_id}`} value={String(c.user_id)}>
                          Generate: {c.company_name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void runBillingNow()}
                      disabled={billingRunning}
                      style={{
                        border: "1px solid #0EA5A0",
                        background: billingRunning ? "#0f172a" : "#042F2E",
                        color: billingRunning ? "#94A3B8" : "#99F6E4",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontWeight: 700,
                        cursor: billingRunning ? "not-allowed" : "pointer",
                      }}
                    >
                    {billingRunning ? "Running..." : "Generate drafts"}
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#0B1220", color: "#94A3B8", textAlign: "left" }}>
                        <th style={{ padding: "10px 14px" }}>Invoice</th>
                        <th style={{ padding: "10px 14px" }}>Period</th>
                        <th style={{ padding: "10px 14px" }}>Savings</th>
                        <th style={{ padding: "10px 14px" }}>Fee</th>
                        <th style={{ padding: "10px 14px" }}>Status</th>
                        <th style={{ padding: "10px 14px" }}>Send</th>
                        <th style={{ padding: "10px 14px" }}>Delete</th>
                        <th style={{ padding: "10px 14px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.invoices.map((inv) => (
                        <tr key={inv.invoice_id} style={{ borderTop: "1px solid #1F355B" }}>
                          <td style={{ padding: "10px 14px", fontWeight: 600 }}>{inv.invoice_id}</td>
                          <td style={{ padding: "10px 14px" }}>{inv.billing_period}</td>
                          <td style={{ padding: "10px 14px", color: "#4ADE80" }}>${inv.total_savings.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 14px", color: "#67E8F9" }}>${inv.fee_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 14px", color: inv.status === "paid" ? "#22C55E" : inv.status === "overdue" ? "#EF4444" : "#FACC15" }}>
                            {inv.status.toUpperCase()}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {inv.status === "draft" && (
                              <button
                                type="button"
                                onClick={() => void sendInvoice(inv.invoice_id)}
                                style={{
                                  border: "1px solid #0EA5A0",
                                  background: "#042F2E",
                                  color: "#99F6E4",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                }}
                              >
                                Send
                              </button>
                            )}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {inv.status === "draft" &&
                              Number(inv.total_savings) === 0 &&
                              Number(inv.fee_amount) === 0 && (
                                <button
                                  type="button"
                                  onClick={() => void deleteInvoice(inv.invoice_id)}
                                  style={{
                                    border: "1px solid #DC2626",
                                    background: "#450A0A",
                                    color: "#FCA5A5",
                                    borderRadius: 8,
                                    padding: "6px 10px",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {inv.status !== "paid" && (
                              <button
                                type="button"
                                onClick={() => void markPaid(inv.invoice_id)}
                                style={{
                                  border: "1px solid #16A34A",
                                  background: "#052E16",
                                  color: "#86EFAC",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                }}
                              >
                                Mark paid
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {!error && adminTab === "agents" && (
          <>
            {(() => {
              const online = agents.filter((a) => a.status === "online").length;
              const offline = agents.filter((a) => a.status === "offline").length;
              const notConnected = agents.filter((a) => a.status === "not_connected").length;
              return (
                <div style={{ ...cardStyle, color: "#CBD5E1", fontSize: 14 }}>
                  <strong style={{ color: "#F8FAFC" }}>{online}</strong> agents online,{" "}
                  <strong style={{ color: "#F8FAFC" }}>{offline}</strong> offline,{" "}
                  <strong style={{ color: "#F8FAFC" }}>{notConnected}</strong> not connected
                </div>
              );
            })()}

            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #1F355B", color: "#F8FAFC", fontWeight: 700 }}>
                Customer agents
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#0B1220", color: "#94A3B8", textAlign: "left" }}>
                      <th style={{ padding: "10px 14px" }}>Customer</th>
                      <th style={{ padding: "10px 14px" }}>Token Name</th>
                      <th style={{ padding: "10px 14px" }}>Status</th>
                      <th style={{ padding: "10px 14px" }}>Scheduler</th>
                      <th style={{ padding: "10px 14px" }}>Mode</th>
                      <th style={{ padding: "10px 14px" }}>Last Seen</th>
                      <th style={{ padding: "10px 14px" }}>Avg Response</th>
                      <th style={{ padding: "10px 14px" }}>Total Pauses</th>
                      <th style={{ padding: "10px 14px" }}>Webhook</th>
                      <th style={{ padding: "10px 14px" }}>Last Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((row) => {
                      const expanded = expandedAgentRows[row.customer_id] === true;
                      const dotColor =
                        row.status === "online"
                          ? "#22C55E"
                          : row.status === "offline"
                          ? "#FACC15"
                          : "#94A3B8";
                      const avgMs = row.response_time_avg_ms;
                      const avgLabel =
                        avgMs >= 1000 ? `${(avgMs / 1000).toFixed(1)}s` : avgMs > 0 ? `${Math.round(avgMs)}ms` : "—";
                      return (
                        <React.Fragment key={row.customer_id}>
                          <tr
                            style={{ borderTop: "1px solid #1F355B", cursor: "pointer" }}
                            onClick={() =>
                              setExpandedAgentRows((prev) => ({
                                ...prev,
                                [row.customer_id]: !prev[row.customer_id],
                              }))
                            }
                          >
                            <td style={{ padding: "10px 14px", color: "#F1F5F9" }}>
                              <div style={{ fontWeight: 600 }}>{row.company_name ?? row.customer_email}</div>
                              <div style={{ fontSize: 12, color: "#64748B" }}>{row.customer_email}</div>
                            </td>
                            <td style={{ padding: "10px 14px", color: "#CBD5E1" }}>{row.token_name ?? "—"}</td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{ color: dotColor, marginRight: 6 }}>●</span>
                              {row.status === "online"
                                ? "Online"
                                : row.status === "offline"
                                ? "Offline"
                                : "Not connected"}
                            </td>
                            <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{row.scheduler ?? "—"}</td>
                            <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{row.mode ?? "—"}</td>
                            <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{formatTs(row.last_seen)}</td>
                            <td style={{ padding: "10px 14px", color: "#67E8F9" }}>{avgLabel}</td>
                            <td style={{ padding: "10px 14px" }}>{row.total_pauses}</td>
                            <td style={{ padding: "10px 14px", color: row.webhook_registered ? "#6EE7B7" : "#94A3B8" }}>
                              {row.webhook_registered ? "Yes" : "No"}
                            </td>
                            <td style={{ padding: "10px 14px", color: "#94A3B8" }}>
                              {row.last_action ? `${row.last_action} · ${formatTs(row.last_action_time)}` : "—"}
                            </td>
                          </tr>
                          {expanded && row.recent_events.length > 0 && (
                            <tr style={{ background: "#0B1220" }}>
                              <td colSpan={10} style={{ padding: "12px 14px" }}>
                                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>Last 5 events</div>
                                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr style={{ color: "#64748B", textAlign: "left" }}>
                                      <th style={{ padding: "4px 8px" }}>Time</th>
                                      <th style={{ padding: "4px 8px" }}>Event</th>
                                      <th style={{ padding: "4px 8px" }}>Jobs</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.recent_events.map((ev) => (
                                      <tr key={`${ev.timestamp}-${ev.event_type}`}>
                                        <td style={{ padding: "4px 8px", color: "#CBD5E1" }}>{formatTs(ev.timestamp)}</td>
                                        <td style={{ padding: "4px 8px" }}>{ev.event_type}</td>
                                        <td style={{ padding: "4px 8px", color: "#94A3B8" }}>
                                          {Array.isArray(ev.payload?.jobs_affected)
                                            ? (ev.payload.jobs_affected as string[]).join(", ")
                                            : "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!error && adminTab === "access-requests" && (
          <>
            <div style={{ ...cardStyle, color: "#CBD5E1", fontSize: 14 }}>
              <strong style={{ color: "#FACC15" }}>{accessRequests?.pending_count ?? 0}</strong>{" "}
              pending request{(accessRequests?.pending_count ?? 0) === 1 ? "" : "s"}
            </div>

            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #1F355B",
                  color: "#F8FAFC",
                  fontWeight: 700,
                }}
              >
                Access requests
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#0B1220", color: "#94A3B8", textAlign: "left" }}>
                      <th style={{ padding: "10px 14px" }}>Company</th>
                      <th style={{ padding: "10px 14px" }}>Email</th>
                      <th style={{ padding: "10px 14px" }}>Use Case</th>
                      <th style={{ padding: "10px 14px" }}>GPU Count</th>
                      <th style={{ padding: "10px 14px" }}>Region</th>
                      <th style={{ padding: "10px 14px" }}>Requested</th>
                      <th style={{ padding: "10px 14px" }}>Status</th>
                      <th style={{ padding: "10px 14px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(accessRequests?.requests ?? []).map((row) => (
                      <tr key={row.id} style={{ borderTop: "1px solid #1F355B" }}>
                        <td style={{ padding: "10px 14px", color: "#F8FAFC", fontWeight: 600 }}>
                          {row.company_name}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#CBD5E1" }}>{row.email}</td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{row.use_case}</td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>
                          {row.estimated_gpu_count}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{row.region}</td>
                        <td style={{ padding: "10px 14px", color: "#94A3B8" }}>
                          {formatTs(row.created_at)}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span
                            style={{
                              ...accessStatusBadge(row.status),
                              borderRadius: 999,
                              padding: "4px 10px",
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          {row.status === "pending" ? (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                disabled={accessActionId === row.id}
                                onClick={() => void approveAccessRequest(row.id)}
                                style={{
                                  border: "1px solid #16A34A",
                                  background: "#052E16",
                                  color: "#86EFAC",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                  cursor: accessActionId === row.id ? "wait" : "pointer",
                                }}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={accessActionId === row.id}
                                onClick={() => void rejectAccessRequest(row.id)}
                                style={{
                                  border: "1px solid #DC2626",
                                  background: "#450A0A",
                                  color: "#FCA5A5",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                  cursor: accessActionId === row.id ? "wait" : "pointer",
                                }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: "#64748B" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(accessRequests?.requests ?? []).length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ padding: "20px 14px", color: "#94A3B8" }}>
                          No access requests yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
